import jwtDecode from 'jwt-decode'

import { calculateExpirationTime } from '@/lib/auth.utils'
import { storages } from '@/lib/browserStorage'
import { ImmutableMap } from '@/lib/immutable/ImmutableMap'
import { ApplicationState } from '@/redux/stores/types'
import { LoginDetails, LoginStatus } from '@/travelsuit'
import { dispatchUserInfo } from '@/travelsuit/users'
import { SessionInfo, TStoreDef } from '@/types/auth'
import { User } from '@/types/user'

import { IAction } from '../actions/action-helpers'
import { AdminUsersTypes, AuthTypes, SupportTypes } from '../actions/actions.types'
import { Actions as AuthActions } from '../actions/auth.actions'
import { clearLocalStorage } from './auth.reducer.helpers'

export type AuthState = ApplicationState['auth']

function saveTokens(decodedHash: Required<auth0.Auth0DecodedHash>): AuthState {
	const expiresAt = calculateExpirationTime(decodedHash)

	const newState: TStoreDef = {
		user: jwtDecode(decodedHash.idToken),
		accessToken: decodedHash.accessToken,
		idToken: decodedHash.idToken,
		expiresAt,
		internalUser: null,
		termsAccepted: null,
		is_impersonation: false,
		user_can_operate: null,
		sessionInfo: {
			valid: false,
			expires_at: new Date(),
		},
		showModal: null,
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return new ImmutableMap<keyof TStoreDef, any>(Object.entries(newState) as [])
}

function setInternalUser(data: { user: User; loginData: Omit<LoginDetails, 'user'> }, state: AuthState) {
	const localStorageInternalUser = storages.local.getItem('internal_user')
	if (!localStorageInternalUser) {
		dispatchUserInfo(data.user)
	}

	storages.local.setItem('internal_user', JSON.stringify(data.user))

	let newState = state.set('internalUser', data.user)
	if (data.loginData) {
		newState = newState
			.set('is_impersonation', data.loginData.is_impersonation)
			.set('user_can_operate', data.loginData.user_can_operate)
	}
	return newState
}

function authReducer(state: AuthState = new ImmutableMap(), action: IAction): AuthState {
	let loginData: LoginDetails
	let sessionInfo: SessionInfo
	switch (action.type) {
		case AuthTypes.LogoutDone:
		case AuthTypes.LoginError:
			return clearLocalStorage()
		case AuthTypes.LoginDone:
			return saveTokens(action.payload)
		case AuthTypes.InternalLogin.SUCCESS: {
			loginData = action.payload
			if (!loginData) {
				return state
			}

			const userExists = loginData.login_status === LoginStatus.UserExists
			const { user, cc_special_access, ...rest } = loginData
			const userWithSpecialAccess = cc_special_access === undefined ? user : { ...user, cc_special_access }

			return authReducer(
				userExists ? authReducer(state, AuthActions.setInternalUser(userWithSpecialAccess, rest)) : state,
				AuthActions.setTermsAccepted(userExists),
			)
		}
		case AuthTypes.SetInternalUser:
			return setInternalUser(action.payload, state)
		case AdminUsersTypes.UpdateUser.SUCCESS: {
			const user = action.payload?.user ?? action.payload
			const userId = user?.id ?? action.requestPayload?.user?.id
			if (userId === state.getWithFallback('internalUser', {}).id) {
				const updatedInternalUser = {
					...(state.get('internalUser') ?? {}),
					...user,
				}
				return setInternalUser({ user: updatedInternalUser, loginData: action.requestPayload?.loginData }, state)
			}
			return state
		}
		case AuthTypes.SetTermsAccepted:
			storages.local.setItem('terms_accepted', String(action.payload))
			return state.set('termsAccepted', action.payload)
		case SupportTypes.ImpersonateSupportCompany.SUCCESS:
			if (action.requestPayload.company) {
				return state.set('is_impersonation', true)
			}
			return state.set('is_impersonation', false)
		case AuthTypes.SessionInfo.SUCCESS:
			sessionInfo = action.payload

			if (state.get('showModal') !== null && sessionInfo.expires_at !== state.get('showModal')) {
				state.set('showModal', null)
			}

			return state.set('sessionInfo', action.payload)
		case AuthTypes.ShowModal:
			return state.set('showModal', state.get('sessionInfo').expires_at)
		case AuthTypes.HideModal:
			return state.set('showModal', null)
		case AuthTypes.SetIsMockEnabled.SUCCESS:
			return authReducer(
				state,
				AuthActions.setInternalUser(
					{
						...state.get('internalUser'),
						mock_enabled: action.payload.mock_enabled,
					},
					{
						login_status: LoginStatus.UserExists,
						is_impersonation: state.get('is_impersonation'),
						user_can_operate: state.get('user_can_operate'),
					},
				),
			)
		case AuthTypes.SetIsTokenizerMockEnabled.SUCCESS:
			return authReducer(
				state,
				AuthActions.setInternalUser(
					{
						...state.get('internalUser'),
						tokenizer_mock_enabled: action.payload.tokenizer_mock_enabled,
					},
					{
						login_status: LoginStatus.UserExists,
						is_impersonation: state.get('is_impersonation'),
						user_can_operate: state.get('user_can_operate'),
					},
				),
			)
		case AuthTypes.SetIsWorldlineMockEnabled.SUCCESS:
			return authReducer(
				state,
				AuthActions.setInternalUser(
					{
						...state.get('internalUser'),
						worldline_mock_enabled: action.payload.worldline_mock_enabled,
					},
					{
						login_status: LoginStatus.UserExists,
						is_impersonation: state.get('is_impersonation'),
						user_can_operate: state.get('user_can_operate'),
					},
				),
			)
		case AuthTypes.SetIsFlightSearchMockEnabled.SUCCESS:
			return authReducer(
				state,
				AuthActions.setInternalUser(
					{
						...state.get('internalUser'),
						flight_search_mock_enabled: action.payload.flight_search_mock_enabled,
					},
					{
						login_status: LoginStatus.UserExists,
						is_impersonation: state.get('is_impersonation'),
						user_can_operate: state.get('user_can_operate'),
					},
				),
			)
		case AuthTypes.SetIsObFeeMockEnabled.SUCCESS: {
			return authReducer(
				state,
				AuthActions.setInternalUser(
					{
						...state.get('internalUser'),
						ob_fee_mock: action.payload.ob_fee_mock,
					},
					{
						login_status: LoginStatus.UserExists,
						is_impersonation: state.get('is_impersonation'),
						user_can_operate: state.get('user_can_operate'),
					},
				),
			)
		}
	}
	return state
}

export default authReducer
